import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { toast } from "sonner";
import { Moon, Users, User, Plus, X, Upload, ArrowLeft, CheckCircle2, XCircle, Copy, Building2, Phone, Ban } from "lucide-react";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const emptyMain = { name: "", national_id: "", phone: "", gender: "male", nationality: "" };
const emptyCompanion = { name: "", national_id: "", phone: "", gender: "male", nationality: "" };

export default function PublicRegistration() {
  const { token } = useParams();
  const [info, setInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [step, setStep] = useState(1); // 1: base data, 2: kind + companions, 3: receipt
  const [main, setMain] = useState(emptyMain);
  const [kind, setKind] = useState("single");
  const [companions, setCompanions] = useState([]);
  const [receipt, setReceipt] = useState(null); // { name, dataUrl }
  const [privateRoom, setPrivateRoom] = useState(false);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null); // { ok, message, campaign_name }
  const [blocked, setBlocked] = useState(null); // { message } when blacklisted

  const proceedToStep2 = async () => {
    try {
      const { data } = await axios.get(`${API}/public/blacklist-check/${encodeURIComponent(main.national_id.trim())}`);
      if (data.blocked) {
        setBlocked({ message: data.message });
        return;
      }
      setStep(2);
    } catch (e) {
      // If check fails, allow progressing; server will still enforce on submit
      setStep(2);
    }
  };

  useEffect(() => {
    axios.get(`${API}/public/campaigns/${token}`)
      .then((r) => {
        setInfo(r.data);
        // Update document title with association name for stronger trust cue
        if (r.data?.association?.name) {
          document.title = `التسجيل في ${r.data.campaign.name} — ${r.data.association.name}`;
        }
      })
      .catch((e) => setError(e?.response?.data?.detail || "الرابط غير صالح"))
      .finally(() => setLoading(false));
  }, [token]);

  const onReceipt = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (f.size > 5 * 1024 * 1024) { toast.error("حجم الملف يجب ألا يتجاوز 5 ميجابايت"); return; }
    const okType = ["image/jpeg", "image/jpg", "image/png", "application/pdf"].includes(f.type);
    if (!okType) { toast.error("الملفات المسموحة: JPG, PNG, PDF"); return; }
    const reader = new FileReader();
    reader.onload = () => setReceipt({ name: f.name, dataUrl: reader.result, type: f.type });
    reader.readAsDataURL(f);
  };

  const addCompanion = () => setCompanions([...companions, { ...emptyCompanion }]);
  const updateCompanion = (i, key, val) => {
    const c = [...companions]; c[i] = { ...c[i], [key]: val }; setCompanions(c);
  };
  const removeCompanion = (i) => setCompanions(companions.filter((_, idx) => idx !== i));

  const submit = async () => {
    if (!receipt) { toast.error("يجب رفع سند التحويل"); return; }
    setBusy(true);
    try {
      const { data } = await axios.post(`${API}/public/campaigns/${token}/register`, {
        ...main,
        registration_kind: kind,
        companions: kind === "family" ? companions : [],
        receipt_base64: receipt.dataUrl,
        private_room: privateRoomActive,
      });
      setResult(data);
    } catch (err) {
      const msg = err?.response?.data?.detail || "حدث خطأ أثناء الإرسال";
      toast.error(typeof msg === "string" ? msg : "خطأ في التسجيل");
    } finally { setBusy(false); }
  };

  if (loading) return <FullScreen><div className="text-emerald-900">جارٍ التحميل...</div></FullScreen>;
  if (error) return <FullScreen>
    <Card className="p-8 max-w-md text-center border-red-200">
      <XCircle className="h-14 w-14 text-red-500 mx-auto mb-4" />
      <h2 className="font-heading text-2xl font-bold text-red-700 mb-2">تعذّر فتح الرابط</h2>
      <p className="text-slate-600">{error}</p>
    </Card>
  </FullScreen>;

  if (blocked) {
    return (
      <FullScreen>
        <Card className="p-10 max-w-md text-center border-amber-300 bg-amber-50">
          <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-amber-100 flex items-center justify-center">
            <Ban className="h-8 w-8 text-amber-700" />
          </div>
          <h2 data-testid="pub-blocked-title" className="font-heading text-2xl font-bold text-amber-900 mb-3">لم يكتمل التسجيل</h2>
          <p data-testid="pub-blocked-msg" className="text-slate-700 text-lg leading-relaxed">{blocked.message}</p>
        </Card>
      </FullScreen>
    );
  }

  if (result) return <ResultView result={result} info={info} />;

  const { campaign, association } = info;
  const requiredUnit = campaign.amount || 0;
  const attendance = campaign.attendance_at ? new Date(campaign.attendance_at) : null;
  const peopleCount = 1 + (kind === "family" ? companions.length : 0);
  const privateRoomPrice = campaign.private_room_price || 0;
  // Determine private-room visibility & force based on scenario:
  //  (1) single female main → FORCED
  //  (2) single male main → hidden
  //  (3) family, 1 companion → based on COMPANION's gender (female=forced, male=optional)
  //  (4) family, 2+ companions → hidden
  let showPrivateRoom = false;
  let privateRoomForced = false;
  if (privateRoomPrice > 0) {
    if (peopleCount === 1 && main.gender === "female") {
      showPrivateRoom = true; privateRoomForced = true;
    } else if (peopleCount === 2) {
      const companionGender = companions[0]?.gender || "male";
      showPrivateRoom = true;
      privateRoomForced = companionGender === "female";
    }
  }
  const privateRoomActive = showPrivateRoom && (privateRoomForced || privateRoom);
  const totalAmount = requiredUnit * peopleCount + (privateRoomActive ? privateRoomPrice : 0);

  // Full capacity screen — no registration allowed
  if (campaign.is_full) {
    return (
      <FullScreen>
        <Card className="p-10 max-w-md text-center border-amber-300 bg-amber-50">
          <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-amber-100 flex items-center justify-center">
            <Users className="h-8 w-8 text-amber-700" />
          </div>
          <h2 data-testid="pub-full-title" className="font-heading text-2xl font-bold text-amber-900 mb-3">اكتمل العدد</h2>
          <p data-testid="pub-full-msg" className="text-slate-700 text-lg">
            نعتذر، اكتمل العدد المسموح به في هذه الحملة.
          </p>
          <p className="text-slate-500 text-sm mt-4">حملة: {campaign.name}</p>
        </Card>
      </FullScreen>
    );
  }

  return (
    <div className="min-h-screen bg-[#FDFBF7]" dir="rtl">
      {/* Header */}
      <header className="bg-emerald-950 text-white">
        <div className="max-w-3xl mx-auto px-4 py-6 flex items-center gap-4">
          {association.logo_base64 ? (
            <img src={association.logo_base64} alt="" className="h-14 w-14 rounded-xl object-cover" />
          ) : (
            <div className="h-14 w-14 rounded-xl bg-amber-500 flex items-center justify-center">
              <Moon className="h-8 w-8 text-emerald-950" />
            </div>
          )}
          <div className="flex-1">
            <div className="font-heading font-bold">{association.name}</div>
            <div className="text-amber-300 text-sm">التسجيل في: {campaign.name}</div>
          </div>
        </div>
      </header>

      {/* Stepper */}
      <div className="max-w-3xl mx-auto px-4 py-6">
        <div className="flex items-center gap-2 mb-6">
          {[1, 2, 3].map((n) => (
            <div key={n} className={`flex-1 h-2 rounded-full ${step >= n ? "bg-emerald-800" : "bg-slate-200"}`} />
          ))}
        </div>
        <div className="text-sm text-slate-500 mb-4">الخطوة {step} من 3</div>

        {step === 1 && (
          <Card className="p-6 border-emerald-900/10">
            <h2 className="font-heading text-2xl font-bold text-emerald-950 mb-1">بياناتك الأساسية</h2>
            <p className="text-slate-500 mb-6 text-sm">من فضلك أدخل بياناتك بشكل صحيح.</p>
            <div className="grid md:grid-cols-2 gap-4">
              <div><Label>الاسم الكامل</Label>
                <Input data-testid="pub-main-name" value={main.name} onChange={(e) => setMain({ ...main, name: e.target.value })} required />
              </div>
              <div><Label>رقم الهوية</Label>
                <Input data-testid="pub-main-id" value={main.national_id} onChange={(e) => setMain({ ...main, national_id: e.target.value })} required />
              </div>
              <div><Label>رقم الجوال</Label>
                <Input data-testid="pub-main-phone" value={main.phone} onChange={(e) => setMain({ ...main, phone: e.target.value })} required />
              </div>
              <div><Label>الجنسية</Label>
                <Input data-testid="pub-main-nationality" value={main.nationality} onChange={(e) => setMain({ ...main, nationality: e.target.value })} required />
              </div>
              <div><Label>الجنس</Label>
                <Select value={main.gender} onValueChange={(v) => setMain({ ...main, gender: v })}>
                  <SelectTrigger data-testid="pub-main-gender"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="male">ذكر</SelectItem>
                    <SelectItem value="female">أنثى</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="mt-6 flex justify-end">
              <Button
                data-testid="pub-step1-next"
                disabled={!main.name || !main.national_id || !main.phone || !main.nationality}
                onClick={proceedToStep2}
                className="bg-emerald-900 hover:bg-emerald-800 text-white">
                التالي <ArrowLeft className="h-4 w-4 mr-2" />
              </Button>
            </div>
          </Card>
        )}

        {step === 2 && (
          <Card className="p-6 border-emerald-900/10">
            <h2 className="font-heading text-2xl font-bold text-emerald-950 mb-1">نوع التسجيل</h2>
            <p className="text-slate-500 mb-6 text-sm">اختر نوع التسجيل. في حالة &quot;عائلة&quot; يمكنك إضافة المرافقين.</p>

            <div className="grid grid-cols-2 gap-4 mb-6">
              <button
                type="button"
                data-testid="pub-kind-single"
                onClick={() => { setKind("single"); setCompanions([]); }}
                className={`p-6 rounded-2xl border-2 transition-all text-right ${kind === "single" ? "border-emerald-700 bg-emerald-50" : "border-slate-200 bg-white hover:border-emerald-300"}`}
              >
                <User className={`h-8 w-8 mb-3 ${kind === "single" ? "text-emerald-700" : "text-slate-400"}`} />
                <div className="font-heading font-bold text-emerald-950">أعزب</div>
                <div className="text-xs text-slate-500 mt-1">تسجيل فردي بدون مرافقين</div>
              </button>
              <button
                type="button"
                data-testid="pub-kind-family"
                onClick={() => setKind("family")}
                className={`p-6 rounded-2xl border-2 transition-all text-right ${kind === "family" ? "border-emerald-700 bg-emerald-50" : "border-slate-200 bg-white hover:border-emerald-300"}`}
              >
                <Users className={`h-8 w-8 mb-3 ${kind === "family" ? "text-emerald-700" : "text-slate-400"}`} />
                <div className="font-heading font-bold text-emerald-950">عائلة</div>
                <div className="text-xs text-slate-500 mt-1">مع مرافقين</div>
              </button>
            </div>

            {kind === "family" && (
              <div className="space-y-4">
                <Card className="p-4 bg-emerald-50 border-emerald-200">
                  <Label className="text-emerald-900 font-bold mb-3 block">كم عدد المرافقين؟</Label>
                  <div className="flex items-center gap-4">
                    <Button
                      type="button"
                      data-testid="pub-companion-minus"
                      onClick={() => setCompanions((prev) => prev.slice(0, Math.max(0, prev.length - 1)))}
                      disabled={companions.length === 0}
                      className="h-12 w-12 bg-emerald-900 hover:bg-emerald-800 text-white text-xl p-0 disabled:opacity-30"
                    >−</Button>
                    <div data-testid="pub-companion-count"
                      className="flex-1 h-12 flex items-center justify-center bg-white rounded-lg border-2 border-emerald-200 font-heading text-2xl font-black text-emerald-900 tabular-nums">
                      {companions.length}
                    </div>
                    <Button
                      type="button"
                      data-testid="pub-companion-plus"
                      onClick={() => setCompanions((prev) => prev.length < 10 ? [...prev, { ...emptyCompanion }] : prev)}
                      disabled={companions.length >= 10}
                      className="h-12 w-12 bg-emerald-900 hover:bg-emerald-800 text-white text-xl p-0 disabled:opacity-30"
                    >+</Button>
                  </div>
                  <p className="text-xs text-slate-500 mt-2">الحد الأقصى: 10 مرافقين</p>
                </Card>

                {companions.length === 0 && (
                  <p className="text-slate-500 text-sm text-center p-4 bg-slate-50 rounded-lg">
                    حدد عدد المرافقين لتظهر لك نماذج الإدخال.
                  </p>
                )}
                {companions.map((c, i) => (
                  <Card key={i} className="p-4 bg-slate-50 border-emerald-900/10">
                    <div className="font-bold text-emerald-950 mb-3">مرافق #{i + 1}</div>
                    <div className="grid md:grid-cols-2 gap-3">
                      <div><Label>الاسم</Label>
                        <Input data-testid={`pub-companion-${i}-name`} value={c.name} onChange={(e) => updateCompanion(i, "name", e.target.value)} required />
                      </div>
                      <div><Label>رقم الهوية</Label>
                        <Input data-testid={`pub-companion-${i}-id`} value={c.national_id} onChange={(e) => updateCompanion(i, "national_id", e.target.value)} required />
                      </div>
                      <div><Label>رقم الجوال</Label>
                        <Input value={c.phone} onChange={(e) => updateCompanion(i, "phone", e.target.value)} />
                      </div>
                      <div><Label>الجنسية</Label>
                        <Input value={c.nationality} onChange={(e) => updateCompanion(i, "nationality", e.target.value)} />
                      </div>
                      <div><Label>الجنس</Label>
                        <Select value={c.gender} onValueChange={(v) => updateCompanion(i, "gender", v)}>
                          <SelectTrigger><SelectValue /></SelectTrigger>
                          <SelectContent>
                            <SelectItem value="male">ذكر</SelectItem>
                            <SelectItem value="female">أنثى</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                    </div>
                  </Card>
                ))}
              </div>
            )}

            <div className="mt-6 flex justify-between">
              <Button type="button" variant="ghost" onClick={() => setStep(1)}>السابق</Button>
              <Button data-testid="pub-step2-next" onClick={() => setStep(3)}
                className="bg-emerald-900 hover:bg-emerald-800 text-white">
                التالي <ArrowLeft className="h-4 w-4 mr-2" />
              </Button>
            </div>
          </Card>
        )}

        {step === 3 && (
          <Card className="p-6 border-emerald-900/10">
            <h2 className="font-heading text-2xl font-bold text-emerald-950 mb-1">تحويل المبلغ ورفع السند</h2>
            <p className="text-slate-500 mb-6 text-sm">حوّل المبلغ إلى الحساب أدناه ثم ارفع صورة/PDF لسند التحويل.</p>

            {(totalAmount > 0 || attendance) && (
              <Card className="p-5 mb-4 bg-amber-50 border-amber-200">
                {totalAmount > 0 && (
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-slate-700">المبلغ الإجمالي المطلوب تحويله</span>
                    <span data-testid="pub-required-amount" className="font-heading text-2xl font-black text-amber-700 tabular-nums">
                      {totalAmount} ر.س
                    </span>
                  </div>
                )}
                {(peopleCount > 1 || privateRoomActive) && requiredUnit > 0 && (
                  <div className="text-xs text-slate-600 mb-2 -mt-1">
                    ({requiredUnit} ر.س × {peopleCount} {peopleCount === 1 ? "شخص" : "أشخاص"}
                    {privateRoomActive ? ` + ${privateRoomPrice} ر.س غرفة خاصة` : ""}
                    {" = "}{totalAmount} ر.س)
                  </div>
                )}
                {showPrivateRoom && (
                  <div className="mt-3 pt-3 border-t border-amber-200">
                    <label className="flex items-start gap-3 cursor-pointer">
                      <input
                        type="checkbox"
                        data-testid="pub-private-room"
                        checked={privateRoomActive}
                        disabled={privateRoomForced}
                        onChange={(e) => setPrivateRoom(e.target.checked)}
                        className="mt-1 h-5 w-5 accent-emerald-700"
                      />
                      <div className="flex-1">
                        <div className="text-emerald-950 font-bold text-sm">
                          أضف غرفة خاصة (+{privateRoomPrice} ر.س)
                        </div>
                        {privateRoomForced && (
                          <div data-testid="pub-private-room-forced" className="text-xs text-amber-700 mt-1">
                            الغرفة الخاصة إلزامية لضمان الخصوصية.
                          </div>
                        )}
                      </div>
                    </label>
                  </div>
                )}
                {attendance && (
                  <div className="flex items-center justify-between text-sm pt-2 border-t border-amber-200 mt-2">
                    <span className="text-slate-700">تاريخ الحضور</span>
                    <span className="font-bold text-emerald-950">
                      {attendance.toLocaleDateString("ar-SA")} — {attendance.toLocaleTimeString("ar-SA", { hour: "2-digit", minute: "2-digit" })}
                    </span>
                  </div>
                )}
              </Card>
            )}

            <Card className="p-5 mb-6 bg-emerald-50 border-emerald-200">
              <div className="flex items-center gap-2 mb-3">
                <Building2 className="h-5 w-5 text-emerald-800" />
                <h3 className="font-heading font-bold text-emerald-950">بيانات التحويل</h3>
              </div>
              <div className="space-y-2 text-sm">
                <BankRow label="اسم البنك" value={association.bank_name} />
                <BankRow label="اسم المستفيد" value={association.bank_beneficiary} />
                <BankRow label="رقم الآيبان (IBAN)" value={association.bank_iban} copy />
                <BankRow label="رقم الحساب" value={association.bank_account} copy />
              </div>
              {association.support_phone && (
                <div className="mt-4 pt-3 border-t border-emerald-200 flex items-center gap-2 text-emerald-800 text-sm">
                  <Phone className="h-4 w-4" />
                  للاستفسار: {association.support_phone}
                </div>
              )}
            </Card>

            <div>
              <Label className="mb-2 block">سند التحويل (JPG / PNG / PDF — حد أقصى 5MB)</Label>
              <label htmlFor="receipt" className="cursor-pointer flex flex-col items-center justify-center border-2 border-dashed border-emerald-900/30 rounded-xl p-8 hover:bg-emerald-50/50 transition-all">
                <Upload className="h-10 w-10 text-emerald-800 mb-3" />
                {receipt ? (
                  <div className="text-center">
                    <div className="text-emerald-800 font-bold">{receipt.name}</div>
                <div className="text-xs text-slate-500 mt-1">تم تحديد الملف — انقر لتغييره</div>
                  </div>
                ) : (
                  <>
                    <div className="text-emerald-900 font-bold">اختر ملف السند</div>
                    <div className="text-xs text-slate-500 mt-1">صورة أو PDF</div>
                  </>
                )}
                <input id="receipt" data-testid="pub-receipt-input" type="file"
                  accept="image/jpeg,image/png,application/pdf" onChange={onReceipt} className="hidden" />
              </label>
            </div>

            <div className="mt-6 flex justify-between">
              <Button type="button" variant="ghost" onClick={() => setStep(2)}>السابق</Button>
              <Button
                data-testid="pub-submit"
                disabled={!receipt || busy}
                onClick={submit}
                className="bg-emerald-900 hover:bg-emerald-800 text-white">
                {busy ? "جارٍ الإرسال..." : "إرسال التسجيل"}
              </Button>
            </div>
          </Card>
        )}
      </div>
    </div>
  );
}

function BankRow({ label, value, copy }) {
  const doCopy = () => {
    if (!value) return;
    navigator.clipboard.writeText(value); toast.success("تم النسخ");
  };
  return (
    <div className="flex items-center justify-between gap-4">
      <span className="text-slate-600">{label}</span>
      <div className="flex items-center gap-2">
        <span className="font-bold text-emerald-950 tabular-nums" dir="ltr">{value || "—"}</span>
        {copy && value && (
          <button onClick={doCopy} className="text-emerald-700 hover:text-emerald-900">
            <Copy className="h-4 w-4" />
          </button>
        )}
      </div>
    </div>
  );
}

function ResultView({ result, info }) {
  return (
    <FullScreen>
      <Card className={`p-10 max-w-md text-center ${result.ok ? "border-emerald-200" : "border-red-200"}`}>
        {result.ok ? (
          <>
            <CheckCircle2 className="h-16 w-16 text-emerald-600 mx-auto mb-4" />
            <h2 data-testid="pub-success-title" className="font-heading text-3xl font-black text-emerald-900 mb-3">تم بحمد الله 🎉</h2>
            <p data-testid="pub-success-msg" className="text-slate-700 text-lg">{result.message}</p>
            <p className="text-slate-500 text-sm mt-4">
              سيقوم فريق {info?.association?.name || "الجمعية"} بمراجعة السند والتواصل معك.
            </p>
            <div className="mt-6 pt-4 border-t border-emerald-100 text-sm text-emerald-800 bg-emerald-50 rounded-lg p-3">
              <b>ما التالي؟</b> فور اعتماد السند من الجمعية ستستلم رابط <b>تذكرتك الرقمية</b> (تصميم بطاقة صعود مع QR) عبر واتساب أو من الجمعية مباشرة.
            </div>
          </>
        ) : (
          <>
            <XCircle className="h-16 w-16 text-red-500 mx-auto mb-4" />
            <h2 data-testid="pub-fail-title" className="font-heading text-2xl font-bold text-red-700 mb-3">لم يكتمل التسجيل</h2>
            <p data-testid="pub-fail-msg" className="text-slate-700">{result.message}</p>
          </>
        )}
      </Card>
    </FullScreen>
  );
}

function FullScreen({ children }) {
  return (
    <div className="min-h-screen bg-[#FDFBF7] flex items-center justify-center p-4" dir="rtl">
      {children}
    </div>
  );
}
